"use client"

import { useState, useEffect } from "react"
import { PenTool, Play, BookOpen, Gamepad2 } from "lucide-react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import { useSeo } from "./hooks/useSeo"
import Navigation, { Footer } from "./Navigation"
import MissiesBanner from "./components/MissiesBanner"
import MissieDemo from "./components/MissieDemo"
import "./css/AboutPage.css"
import "./css/MissieDemo.css"
import "./css/MissiesPage.css"

// The Missies page explains the game to parents (#427): what's in it, what
// a child learns, a few of the videos, then how to start. Only App
// Ontwerper is finished, so the page talks about gameplay and skills, not
// about chapters.

// Videos are Dutch-only. The two tips are the game's own tip videos
// (public/games/missies/content/tips/); the design thinking one is not in
// the game. Posters are small JPGs made for this page.
const FEATURED_VIDEO = { src: "/shorts/nl/designthinking-gym.mp4", poster: "/shorts/nl/designthinking-gym.jpg" }
const TIP_VIDEOS = [
  { src: "/shorts/nl/app-oneperson.mp4", poster: "/shorts/nl/app-oneperson.jpg" },
  { src: "/shorts/nl/5keerwaarom.mp4", poster: "/shorts/nl/5keerwaarom.jpg" },
]

const INSIDE_ICONS = [PenTool, Play, BookOpen, Gamepad2]

const COPY = {
  nl: {
    seoTitle: "Techtok Missies: leer digitaal maken door te spelen",
    seoDescription:
      "Zo speelt je kind Techtok Missies: korte missies waarin het apps repareert, schurk Crasher verslaat en stap voor stap een eigen app ontwerpt. Probeer 7 dagen gratis.",
    bannerTitle: "Zo speelt je kind Techtok Missies",
    bannerSubtitle:
      "Korte missies waarin je kind apps repareert, schurk Crasher verslaat en stap voor stap een eigen app ontwerpt. Probeer het zelf in het scherm: dit is precies wat je kind speelt.",
    play: "Speel Nu",
    trial: "Probeer 7 dagen gratis",
    insideTitle: "Wat zit er in Techtok Missies?",
    insideSubtitle: "Alles hangt aan één verhaal: schurk Crasher roept steeds het verkeerde, en je kind bewijst hem ongelijk.",
    inside: [
      { title: "De Makerij", text: "Hier ontwerpt je kind een eigen app-idee: welk probleem het oplost, voor wie het is en hoe de schermen eruitzien." },
      { title: "Video-tips", text: "Korte filmpjes die één slim idee uitleggen, zoals vijf keer 'waarom?' vragen om het echte probleem te vinden." },
      { title: "Korte uitleg", text: "Lessen die uitleggen hoe ontwerpers werken, voordat je kind het zelf probeert. Na elk antwoord vertelt de game ook waarom." },
      { title: "Mini-games", text: "Rondes met vragen: tik het juiste deel van een scherm aan, zet schermen in volgorde of repareer wat kapot is. Goede antwoorden spelen nieuwe vaardigheden vrij." },
    ],
    skillsTitle: "Wat je kind leert: denken als een ontwerper",
    skillsSubtitle: "Dezelfde stappen die echte ontwerpers zetten, ook wel design thinking genoemd. Je kind speelt ze één voor één vrij.",
    // Plain words first, the name the game uses after.
    skills: [
      { text: "Uitzoeken wat er echt misgaat bij iemand", name: "Probleem definiëren" },
      { text: "Bedenken voor wie de app is (dat ben je vaak niet zelf)", name: "Voor wie bouw je?" },
      { text: "Veel ideeën verzinnen en dan de beste kiezen", name: "Ideeën bedenken" },
      { text: "Het idee eerst op papier schetsen", name: "Schetsen" },
      { text: "De schermen in een logische volgorde zetten", name: "Flow logica" },
      { text: "Snel een simpele versie maken om uit te proberen", name: "Prototypen" },
      { text: "Laten testen en leren van wat een ander niet snapt", name: "Testen" },
    ],
    videosTitle: "Zo leggen we het uit",
    videosNote: "",
    featured: { title: "Wat is design thinking?", text: "Een animatie van ruim 2 minuten." },
    tipsTitle: "Video-tips uit de game",
    tips: [
      { title: "Ontwerp voor één persoon", text: "Eén echte gebruiker blij maken verslaat honderd 'misschien'-gebruikers." },
      { title: "Vijf keer waarom", text: "Vraag vijf keer 'waarom?' en je vindt het echte probleem, niet alleen het symptoom." },
    ],
    ctaTitle: "Klaar om te beginnen?",
    ctaText: "Elk plan begint met 7 dagen gratis. Zonder betaalgegevens.",
  },
  en: {
    seoTitle: "Techtok Missions: learn to make things digitally by playing",
    seoDescription:
      "How your child plays Techtok Missions: short missions where they fix apps, beat the villain Crasher and design their own app step by step. Try 7 days free.",
    bannerTitle: "How your child plays Techtok Missions",
    bannerSubtitle:
      "Short missions where your child fixes apps, beats the villain Crasher and designs their own app step by step. Try it yourself on the screen: this is exactly what your child plays.",
    play: "Play Now",
    trial: "Try 7 days free",
    insideTitle: "What's inside Techtok Missions?",
    insideSubtitle: "It all hangs on one story: the villain Crasher keeps getting things wrong, and your child proves him wrong.",
    inside: [
      { title: "The Makerij", text: "Here your child designs their own app idea: which problem it solves, who it is for and what the screens look like." },
      { title: "Video tips", text: "Short videos that explain one smart idea, like asking 'why?' five times to find the real problem." },
      { title: "Short lessons", text: "Lessons that explain how designers work, before your child tries it. After every answer the game also explains why." },
      { title: "Mini-games", text: "Rounds with questions: tap the right part of a screen, put screens in order or fix what is broken. Good answers unlock new skills." },
    ],
    skillsTitle: "What your child learns: thinking like a designer",
    skillsSubtitle: "The same steps real designers take, also known as design thinking. Your child unlocks them one by one.",
    skills: [
      { text: "Find out what really goes wrong for someone", name: "Defining the problem" },
      { text: "Work out who the app is for (usually not yourself)", name: "Who are you building for?" },
      { text: "Come up with lots of ideas, then pick the best", name: "Coming up with ideas" },
      { text: "Sketch the idea on paper first", name: "Sketching" },
      { text: "Put the screens in a logical order", name: "Flow logic" },
      { text: "Quickly make a simple version to try out", name: "Prototyping" },
      { text: "Let someone test it and learn from what they don't get", name: "Testing" },
    ],
    videosTitle: "How we explain it",
    videosNote: "The videos are in Dutch.",
    featured: { title: "What is design thinking?", text: "An animation of just over 2 minutes." },
    tipsTitle: "Video tips from the game",
    tips: [
      { title: "Design for one person", text: "Making one real user happy beats a hundred 'maybe' users." },
      { title: "Five times why", text: "Ask 'why?' five times and you find the real problem, not just the symptom." },
    ],
    ctaTitle: "Ready to start?",
    ctaText: "Every plan starts with 7 days free. No payment details needed.",
  },
}

const MissiesPage = ({ onReturnHome }: { onReturnHome?: () => void }) => {
  const { language } = useLanguage()
  const [appConfig, setAppConfig] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const c = language.code === "nl" ? COPY.nl : COPY.en

  const t = (path: string, defaultText = "") => {
    if (!appConfig) return defaultText
    const keys = path.split(".")
    let value: any = appConfig
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }
    if (value && typeof value === "object" && value[language.code]) return value[language.code]
    return typeof value === "string" ? value : value?.en || defaultText
  }

  useSeo({ path: "/missies", title: c.seoTitle, description: c.seoDescription })

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        setAppConfig(await loadAppConfiguration(language.code, null, false))
      } catch (e) {
        console.error("Kon Missies-pagina niet laden:", e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [language.code])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div className="landing-selector-container">
      <Navigation appConfig={appConfig} currentPage="none" onNavigate={() => {}} t={t} />

      <main className="main-content">
        <MissiesBanner
          data={{
            title: c.bannerTitle,
            subtitle: c.bannerSubtitle,
            ctaButton: c.play,
            ctaLink: "/games/missies/",
            trialButton: c.trial,
            trialLink: "/portal/register.php",
          }}
          languageCode={language.code}
          atTop
        >
          <div className="missies-page-demo">
            <MissieDemo conceptId="app-basics" loop={1} />
          </div>
        </MissiesBanner>

        <section className="missies-section">
          <h2 className="missies-section-title">{c.insideTitle}</h2>
          <p className="missies-section-subtitle">{c.insideSubtitle}</p>
          <div className="missies-inside-grid">
            {c.inside.map((item, i) => {
              const Icon = INSIDE_ICONS[i]
              return (
                <div key={item.title} className="missies-inside-card">
                  <div className="missies-inside-icon"><Icon /></div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              )
            })}
          </div>
        </section>

        <div className="missies-skills-band">
          <section className="missies-section">
            <h2 className="missies-section-title">{c.skillsTitle}</h2>
            <p className="missies-section-subtitle">{c.skillsSubtitle}</p>
            <ol className="missies-skills-list">
              {c.skills.map((skill, i) => (
                <li key={skill.name} className="missies-skill">
                  <span className="missies-skill-number">{i + 1}</span>
                  <span className="missies-skill-body">
                    <strong>{skill.text}</strong>
                    <span>{skill.name}</span>
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <section className="missies-section missies-videos-section">
          <h2 className="missies-section-title">{c.videosTitle}</h2>
          {c.videosNote && <p className="missies-section-subtitle">{c.videosNote}</p>}
          <figure className="missies-video missies-video--wide">
            <video controls preload="none" poster={FEATURED_VIDEO.poster} src={FEATURED_VIDEO.src} />
            <figcaption>
              <strong>{c.featured.title}</strong>
              {c.featured.text}
            </figcaption>
          </figure>

          <h3 className="missies-videos-tips-title">{c.tipsTitle}</h3>
          <div className="missies-video-tips">
            {c.tips.map((tip, i) => (
              <figure key={tip.title} className="missies-video missies-video--tall">
                <video controls preload="none" poster={TIP_VIDEOS[i].poster} src={TIP_VIDEOS[i].src} />
                <figcaption>
                  <strong>{tip.title}</strong>
                  {tip.text}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="missies-cta-section">
          <h2>{c.ctaTitle}</h2>
          <p>{c.ctaText}</p>
          <div className="missies-cta-actions">
            <a href="/portal/register.php" className="missies-banner-cta">{c.trial}</a>
            <a href="/games/missies/" className="missies-banner-cta missies-banner-cta--ghost">{c.play}</a>
          </div>
        </section>
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default MissiesPage
